import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, X, Send, Loader2, ArrowRight } from "lucide-react";
import { streamAssistantChat } from "@/lib/api";

const SUGGESTED = [
  "What services does RockLadder offer?",
  "How does your 24×7 SOC work?",
  "Tell me about your Cyber Resilience Framework",
  "Which technology partners do you work with?",
];

const SESSION_KEY = "rl_assistant_session_v1";

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    try {
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) setSessionId(existing);
    } catch {}
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: msg },
      { role: "assistant", content: "", sources: [], _streaming: true },
    ]);
    setStreaming(true);
    let acc = "";
    let sources = [];
    abortRef.current = streamAssistantChat({
      sessionId,
      message: msg,
      onEvent: ({ type, data }) => {
        if (type === "sources") {
          sources = Array.isArray(data) ? data : [];
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], sources };
            return copy;
          });
        } else if (type === "token") {
          const delta = (data && data.delta) || "";
          acc += delta;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc };
            return copy;
          });
        } else if (type === "done") {
          if (data && data.session_id) {
            setSessionId(data.session_id);
            try { sessionStorage.setItem(SESSION_KEY, data.session_id); } catch {}
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], _streaming: false };
            return copy;
          });
          setStreaming(false);
        } else if (type === "error") {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              ...copy[copy.length - 1],
              content: acc || "I couldn't reach the model. Please try again in a moment.",
              _streaming: false,
              _error: true,
            };
            return copy;
          });
          setStreaming(false);
        }
      },
    });
  };

  useEffect(() => () => { if (abortRef.current) abortRef.current(); }, []);

  return (
    <>
      {/* Floating button */}
      <motion.button
        type="button"
        data-testid="assistant-fab"
        onClick={() => setOpen((v) => !v)}
        initial={reduced ? false : { scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        aria-label={open ? "Close assistant" : "Open RockLadder AI assistant"}
        className="fixed bottom-6 right-6 z-[70] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#111111] text-white shadow-[0_16px_44px_-12px_rgba(0,0,0,0.35)] hover:bg-[#5B2A86] transition-colors"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && (
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#F5B841] ring-2 ring-white" />
        )}
      </motion.button>

      {/* Panel */}
      {open && (
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="assistant-panel"
          className="fixed z-[69] bottom-24 right-6 w-[min(96vw,400px)] h-[min(78vh,620px)] rounded-2xl bg-white border border-[rgba(17,17,17,0.10)] rl-chat-open-shadow flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[rgba(17,17,17,0.08)] flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-[#111111] text-white grid place-items-center font-mono text-[10px] tracking-widest">RA</div>
            <div className="flex-1">
              <div className="font-editorial-display text-[15px] leading-none text-[#111]">RockAssist</div>
              <div className="font-mono text-[10px] tracking-widest text-[#8A8A8A] mt-1">RockLadder · AI advisor</div>
            </div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-[#5B2A86]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#5B2A86] animate-pulse" />
              CLAUDE·SONNET·5
            </span>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-[#FBF9F4]">
            {messages.length === 0 && (
              <div className="space-y-4" data-testid="assistant-empty">
                <p className="font-editorial-display text-[22px] leading-[1.15] text-[#111]">
                  Hi. Ask about our services, SOC, partners or how we can help.
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {SUGGESTED.map((s, i) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`assistant-suggested-${i}`}
                      onClick={() => send(s)}
                      className="text-left px-3 py-2 rounded-lg bg-white border border-[rgba(17,17,17,0.08)] hover:border-[#5B2A86] hover:text-[#5B2A86] transition-colors text-[13px] text-[#2A2A2A] flex items-center justify-between gap-2"
                    >
                      <span>{s}</span>
                      <ArrowRight className="h-3.5 w-3.5 opacity-60" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[80%] rounded-2xl bg-[#111] text-white px-3.5 py-2 text-[13.5px] leading-[1.5]"
                      : "max-w-[92%] text-[13.5px] leading-[1.6] text-[#1A1A1A]"
                  }
                >
                  {m.role === "assistant" ? (
                    <>
                      <div className="whitespace-pre-wrap">{m.content || (m._streaming ? "" : "")}
                        {m._streaming && (
                          <span className="inline-block w-1.5 h-3.5 align-middle ml-0.5 bg-[#5B2A86] animate-pulse" />
                        )}
                      </div>
                      {m.sources && m.sources.length > 0 && !m._streaming && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {m.sources.slice(0, 3).map((s, j) => (
                            <a
                              key={j}
                              href={s.url}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[rgba(17,17,17,0.10)] font-mono text-[9.5px] tracking-widest text-[#5B2A86] hover:border-[#5B2A86]"
                            >
                              {s.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[rgba(17,17,17,0.08)] px-3 py-3 bg-white flex items-end gap-2"
          >
            <textarea
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
              placeholder="Ask about our services, SOC, partners..."
              className="flex-1 resize-none max-h-24 rounded-xl border border-[rgba(17,17,17,0.12)] focus:border-[#5B2A86] focus:outline-none px-3 py-2 text-[13.5px] bg-[#FBF9F4]"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="assistant-send"
              className="h-10 w-10 rounded-xl bg-[#111] text-white grid place-items-center disabled:opacity-40 hover:bg-[#5B2A86] transition-colors"
              aria-label="Send"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </motion.div>
      )}
    </>
  );
}
